import { t } from 'i18next';
import {
  ECheckoutSource,
  EPlanIntervalUnit,
  IPlan,
} from '../../models/checkout.model';
import { stringsUtil, logger } from '../../utils';
import { apiPremium } from '../../utils/api/apiPremium';
import { TranslationKey } from '@/types/i18n.types';
import { LOGGER_ACTION, LOGGER_FEATURE } from '@/constants/logger/logger-tags';

const logIfPlanCurrenciesMismatch = (plans: IPlan[]): void => {
  if (plans.length < 2) return;

  const baseCurrency = plans[0].currency;
  const hasMismatch = plans.some(p => p.currency !== baseCurrency);
  if (!hasMismatch) return;

  logger.warn('Pricing plans have inconsistent currencies', {
    tags: {
      feature: LOGGER_FEATURE.CHECKOUT,
      action: LOGGER_ACTION.LOAD_PLANS,
    },
    extra: {
      baseCurrency,
      plans: plans.map(p => ({
        planName: p.plan_name,
        currency: p.currency,
      })),
    },
  });
};

export const useCheckout = () => {
  const getCheckoutFlowPlans = async (
    source: string = ECheckoutSource.premiumModal
  ): Promise<IPlan[]> => {
    const plans = await apiPremium.getSubscriptionPlans(source);
    logIfPlanCurrenciesMismatch(plans);
    return plans;
  };

  const getPlanTitle = (plan: IPlan): string => {
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return t('checkout.choosePlan.onetime');
    }
    if (plan.interval_unit === EPlanIntervalUnit.YEAR) {
      return t('checkout.choosePlan.12Months');
    }
    return `${plan.interval_count} ${t('checkout.choosePlan.month')}${plan.interval_count > 1 ? 's' : ''}`;
  };

  const getPlanPeriodLabel = (plan: IPlan): string => {
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return t('checkout.oneTimePayment');
    }
    return '/' + t('checkout.choosePlan.month');
  };

  const getPlanPriceLabel = (plan: IPlan): string => {
    const currencySymbol = stringsUtil.symbolByCurrency(plan.currency);
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return `${currencySymbol}${plan.price}`;
    }
    return `${currencySymbol}${plan.price_per_month}`;
  };

  const getPaymentSummaryPlanDescription = (plan: IPlan): string => {
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return t('checkout.lifeTimePlan');
    }
    if (plan.interval_unit === EPlanIntervalUnit.YEAR) {
      return '12 Months Subscription Plan';
    }
    return `${plan.interval_count} Months Subscription Plan`;
  };

  const getPaymentPlanSummaryDescription = (plan: IPlan): string => {
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return t('checkout.paymentSummaryDescriptionLifetime');
    }
    const currencySymbol = stringsUtil.symbolByCurrency(plan.currency);
    const months =
      plan.interval_unit === EPlanIntervalUnit.YEAR ? 12 : plan.interval_count;
    return (
      currencySymbol +
      t('checkout.paymentSummaryDescription', {
        price: plan.price_per_month,
        months: months,
      })
    );
  };

  const getSummaryPrice = (plan: IPlan): string => {
    const currencySymbol = stringsUtil.symbolByCurrency(plan.currency);
    return `${currencySymbol}${plan.price}`;
  };

  const getPlanPricePerMonthLabel = (plan: IPlan): string => {
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return t('checkout.once');
    }
    return t('checkout.perMonth');
  };

  const getPriceDescription = (plan: IPlan): string => {
    const currencySymbol = stringsUtil.symbolByCurrency(plan.currency);
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return t('checkout.choosePlan.noFuturePaymentsDescription');
    }
    if (plan.interval_unit === EPlanIntervalUnit.YEAR) {
      return t('checkout.choosePlan.yearlyPriceDescription', {
        price: currencySymbol + plan.price,
      });
    }
    if (plan.interval_count > 1) {
      return t('checkout.choosePlan.semiYearlyPriceDescription', {
        price: currencySymbol + plan.price,
        interval_count: plan.interval_count,
      });
    }
    return t('checkout.choosePlan.monthlyPriceDescription');
  };

  const getPlanButtonText = (plan: IPlan): TranslationKey => {
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return 'checkout.upgradeOnceEnjoyAlways';
    }
    if (plan.trial_period_in_days > 0) {
      return t('purchasePagePayment.TryDaysFree', {
        trialDays: plan.trial_period_in_days,
      });
    }
    return 'checkout.subscribeNow';
  };

  const getPaymentTitle = (plan: IPlan) => {
    let period;
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      period = t('checkout.lifeTime');
    } else {
      if (plan.interval_unit === EPlanIntervalUnit.YEAR) {
        period = t('checkout.choosePlan.12Months');
      } else {
        period = plan.interval_count.toString();
      }
    }
    return t('checkout.paymentTitle', { period: period });
  };

  return {
    getPlanPeriodLabel,
    getPaymentPlanSummaryDescription,
    getSummaryPrice,
    getPaymentSummaryPlanDescription,
    getPlanPricePerMonthLabel,
    getCheckoutFlowPlans,
    getPlanTitle,
    getPlanPriceLabel,
    getPriceDescription,
    getPlanButtonText,
    getPaymentTitle,
  };
};
